import React from 'react';
import { FaChevronLeft, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';
import { useLanguage } from '../../App';
import './Pagination.css';

const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  showItemsPerPage = true,
  showInfo = true,
  className = '',
  disabled = false
}) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  // Don't render if there's only one page or no items
  if (totalPages <= 1 && totalItems <= itemsPerPage) {
    return null;
  }

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage && !disabled) {
      onPageChange(page);
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    if (onItemsPerPageChange && !disabled) {
      onItemsPerPageChange(newItemsPerPage);
    }
  };

  // Calculate visible page numbers
  const getVisiblePages = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    // Always include first page
    range.push(1);

    // Add pages around current page
    for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
      range.push(i);
    }

    // Always include last page if there's more than one page
    if (totalPages > 1) {
      range.push(totalPages);
    }

    // Remove duplicates and sort
    const uniqueRange = [...new Set(range)].sort((a, b) => a - b);

    // Add dots where there are gaps
    let prev = 0;
    uniqueRange.forEach((page) => {
      if (page - prev > 1) {
        rangeWithDots.push('...');
      }
      rangeWithDots.push(page);
      prev = page;
    });

    return rangeWithDots;
  };

  const visiblePages = getVisiblePages();

  // Calculate display info
  const fromItem = totalItems > 0 ? ((currentPage - 1) * itemsPerPage) + 1 : 0;
  const toItem = Math.min(currentPage * itemsPerPage, totalItems);

  const itemsPerPageOptions = [5, 10, 20, 50];

  return (
    <div className={`pagination-container ${isRTL ? 'rtl' : 'ltr'} ${className}`}>
      {/* Items per page selector */}
      {showItemsPerPage && (
        <div className="items-per-page">
          <label className="items-per-page-label">
            {language === 'ar' ? 'عرض:' : 'Show:'}
          </label>
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
            className="items-per-page-select"
            disabled={disabled}
          >
            {itemsPerPageOptions.map(option => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <span className="items-per-page-text">
            {language === 'ar' ? 'عنصر لكل صفحة' : 'items per page'}
          </span>
        </div>
      )}

      {/* Pagination info */}
      {showInfo && (
        <div className="pagination-info">
          {language === 'ar' ? (
            <span>
              عرض {fromItem} إلى {toItem} من أصل {totalItems} عنصر
            </span>
          ) : (
            <span>
              Showing {fromItem} to {toItem} of {totalItems} items
            </span>
          )}
        </div>
      )}

      {/* Pagination controls */}
      <div className="pagination-controls">
        {/* First page button */}
        <button
          className={`pagination-btn first-btn ${currentPage === 1 ? 'disabled' : ''}`}
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1 || disabled}
          title={language === 'ar' ? 'الصفحة الأولى' : 'First page'}
        >
          {isRTL ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />}
        </button>

        {/* Previous page button */}
        <button
          className={`pagination-btn prev-btn ${currentPage === 1 ? 'disabled' : ''}`}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || disabled}
          title={language === 'ar' ? 'الصفحة السابقة' : 'Previous page'}
        >
          {isRTL ? <FaChevronRight /> : <FaChevronLeft />}
        </button>

        {/* Page numbers */}
        <div className="page-numbers">
          {visiblePages.map((page, index) => (
            page === '...' ? (
              <span key={`dots-${index}`} className="pagination-dots">
                ...
              </span>
            ) : (
              <button
                key={page}
                className={`pagination-btn page-btn ${currentPage === page ? 'active' : ''}`}
                onClick={() => handlePageChange(page)}
                disabled={disabled}
              >
                {page}
              </button>
            )
          ))}
        </div>

        {/* Next page button */}
        <button
          className={`pagination-btn next-btn ${currentPage === totalPages ? 'disabled' : ''}`}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || disabled}
          title={language === 'ar' ? 'الصفحة التالية' : 'Next page'}
        >
          {isRTL ? <FaChevronLeft /> : <FaChevronRight />}
        </button>

        {/* Last page button */}
        <button
          className={`pagination-btn last-btn ${currentPage === totalPages ? 'disabled' : ''}`}
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages || disabled}
          title={language === 'ar' ? 'الصفحة الأخيرة' : 'Last page'}
        >
          {isRTL ? <FaAngleDoubleLeft /> : <FaAngleDoubleRight />}
        </button>
      </div>
    </div>
  );
};

export default Pagination;
